/**
 * taminzo-filters.js — reusable storefront filter bar, a faithful clone of Globo's filter DOM.
 *
 * Emits Globo's EXACT `.gf-option-block` markup (checkbox / colour-swatch / price-slider) so the copied
 * Globo CSS (taminzo-globo.css + taminzo-globo.slider.css) styles it byte-identically — and it survives
 * Globo's removal (no dependency on Globo's app JS/CSS). Only values present in the current result set are
 * shown, each with its count (Winter (4)). Multi-select (OR within a filter, AND across filters) + a price
 * noUiSlider. Filter selections live in the page URL so refresh/share/back restore them.
 *
 * TRANSPORT-AGNOSTIC so it reuses on collection pages later: the HOST owns fetching /results + rendering
 * cards + pagination; this module owns the filter UI + selected state + the results URL. The always-on
 * "mega filter" scope is generic — search page passes base="q=…", a collection page passes base="collection=…".
 *
 *   var f = TaminzoFilters.create({ api, mountEl, market, base, onChange });
 *   host fetches:  f.resultsPath(page, limit)
 *   on page-1:     f.setFacets(data.facets, data.priceMin, data.priceMax)
 *
 * Requires (loaded before this): taminzo-nouislider.js (window.noUiSlider) for the price slider.
 */
(function () {
  'use strict';

  // iOS Safari auto-zooms into any input whose font-size < 16px — the zoom scrolls the product grid into
  // view, makes the page wider than the viewport (horizontal scroll) and stays zoomed. Globo's CSS sets the
  // filter inputs to 13px, so force them to 16px. `!important` (Globo's rule isn't important) guarantees the
  // override wins regardless of specificity/source-order, and NO media query so a viewport quirk can't skip
  // it — 16px on desktop in a filter box is imperceptible and desktop doesn't auto-zoom anyway.
  (function () {
    try {
      var id = 'tzf-nozoom-style';
      if (document.getElementById(id)) return;
      var s = document.createElement('style');
      s.id = id;
      s.textContent = '.gf-range-inputs input,.gf-controls-search-input,.gf-option-search input{font-size:16px!important}';
      (document.head || document.documentElement).appendChild(s);
    } catch (e) {}
  })();

  // Globo's colour → rgb map (lifted verbatim from Globo's rendered swatches). Keyed by lowercased value.
  var SWATCH = {
    'beige': '245,245,220', 'black': '0,0,0', 'blue': '11,21,193', 'brown': '150,75,0', 'burgundy': '128,0,32',
    'champagne': '247,231,206', 'charcoal': '54,69,79', 'coral': '255,127,80', 'cream': '255,253,208', 'gold': '255,215,0',
    'green': '12,202,12', 'grey': '128,128,128', 'gray': '128,128,128', 'ivory': '255,214,153', 'lilac': '200,162,200',
    'maroon': '128,0,0', 'mint green': '62,180,137', 'mulberry': '204,0,126', 'olive': '128,128,0', 'orange': '255,165,0',
    'peach': '255,229,180', 'pink': '255,192,203', 'purple': '160,32,240', 'red': '255,0,0', 'salmon pink': '250,128,114',
    'silver': '192,192,192', 'taupe': '72,60,50', 'white': '255,255,254', 'yellow': '246,246,35'
  };

  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function enc(s) { return encodeURIComponent(s); }
  // A DOM-id-safe token from a filter key / value.
  function idsafe(s) { return String(s).replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase(); }

  // /filters config is shared across controllers — fetch once.
  var _configPromise = null;
  var _mobileLayout = 'drawer';   // 'drawer' (Filter button + side menu) | 'swimlane' (horizontal chip row). Admin-set.
  function loadConfig(api) {
    if (!_configPromise) {
      _configPromise = fetch(api + '/storefront/search/filters', { mode: 'cors', credentials: 'omit' })
        .then(function (r) { return r.ok ? r.json() : { filters: [] }; })
        .then(function (d) {
          if (d && (d.mobileFilterLayout === 'swimlane' || d.mobileFilterLayout === 'drawer')) _mobileLayout = d.mobileFilterLayout;
          return (d && d.filters) || [];
        })
        .catch(function () { return []; });
    }
    return _configPromise;
  }

  function create(opts) {
    opts = opts || {};
    var api = opts.api;
    var mount = opts.mountEl;
    var onChange = typeof opts.onChange === 'function' ? opts.onChange : function () {};
    var base = opts.base || '';
    var market = opts.market || 'PK';
    // Results endpoint — search by default; collection pages pass '/storefront/collection/results'.
    var resultsEndpoint = opts.path || '/storefront/search/results';
    // Optional row the "Filter" trigger is placed into (so it can sit in parallel with the Sort widget).
    // When set, the trigger is prepended to it (Filter left) and the main-screen chips go BELOW that row.
    var triggerHost = opts.triggerHost || null;

    var config = null;
    var facets = {};
    var bounds = { min: null, max: null };
    var sel = {};            // key -> [values]
    var priceSel = null;     // {min,max} | null
    var sortValue = '';      // '' = Recommended
    var configKeys = [];
    var facetSig = '';
    var sliders = {};        // key -> noUiSlider instance

    // Sort options (Globo's list, minus Featured/Availability which don't apply to search). Value → backend sort key.
    var SORT_OPTIONS = [
      { v: '', label: 'Recommended' },
      { v: 'best-selling', label: 'Best Selling' },
      { v: 'title-asc', label: 'Alphabetically, A-Z' },
      { v: 'title-desc', label: 'Alphabetically, Z-A' },
      { v: 'price-asc', label: 'Price, low to high' },
      { v: 'price-desc', label: 'Price, high to low' },
      { v: 'newest', label: 'Date, new to old' },
      { v: 'oldest', label: 'Date, old to new' },
      { v: 'discount', label: 'Discount: high to low' }
    ];
    function sortLabel(v) { for (var i = 0; i < SORT_OPTIONS.length; i++) if (SORT_OPTIONS[i].v === v) return SORT_OPTIONS[i].label; return 'Recommended'; }

    // ── URL state ──────────────────────────────────────────────────────────
    // Non-filter params. Everything else in the URL is a filter key → so readUrl() works WITHOUT waiting for
    // the (async) config to load, which is what lets a refresh apply the URL filters on the very first fetch.
    // `img` = the visual-search session id (image mode): a scope param like `q`, NOT a filter — so it's
    // preserved in the URL when filters change and never rendered as a filter chip.
    var RESERVED = { q: 1, img: 1, market: 1, page: 1, limit: 1, priceMin: 1, priceMax: 1, sort: 1 };
    function readUrl() {
      var qs = new URLSearchParams(location.search);
      sel = {}; priceSel = null;
      qs.forEach(function (v, k) {
        if (RESERVED[k]) return;
        if (v) sel[k] = v.split(',').map(function (x) { return x.trim(); }).filter(Boolean);
      });
      var pmin = qs.get('priceMin'), pmax = qs.get('priceMax');
      if (pmin != null || pmax != null) priceSel = { min: pmin != null ? +pmin : null, max: pmax != null ? +pmax : null };
      sortValue = qs.get('sort') || '';
    }
    function writeUrl() {
      var qs = new URLSearchParams(location.search);
      var stale = []; qs.forEach(function (v, k) { if (!RESERVED[k]) stale.push(k); });
      stale.forEach(function (k) { qs.delete(k); });
      qs.delete('priceMin'); qs.delete('priceMax');
      Object.keys(sel).forEach(function (k) { if (sel[k] && sel[k].length) qs.set(k, sel[k].join(',')); });
      if (priceSel) { if (priceSel.min != null) qs.set('priceMin', priceSel.min); if (priceSel.max != null) qs.set('priceMax', priceSel.max); }
      if (sortValue) qs.set('sort', sortValue); else qs.delete('sort');
      var str = qs.toString();
      try { history.replaceState(null, '', location.pathname + (str ? '?' + str : '') + location.hash); } catch (e) {}
    }

    function getParams() {
      var parts = [];
      Object.keys(sel).forEach(function (k) { if (sel[k] && sel[k].length) parts.push(enc(k) + '=' + enc(sel[k].join(','))); });
      if (priceSel) {
        if (priceSel.min != null) parts.push('priceMin=' + enc(priceSel.min));
        if (priceSel.max != null) parts.push('priceMax=' + enc(priceSel.max));
      }
      return parts.length ? '&' + parts.join('&') : '';
    }
    function resultsPath(page, limit) {
      return resultsEndpoint + '?' + base + (base ? '&' : '') +
        'market=' + enc(market) + '&page=' + page + '&limit=' + limit + getParams() +
        (sortValue ? '&sort=' + enc(sortValue) : '');
    }
    function setBase(b, m) { if (b != null) base = b; if (m != null) market = m; }
    function hasActive() {
      if (priceSel) return true;
      return Object.keys(sel).some(function (k) { return sel[k] && sel[k].length; });
    }

    // ── Which values does this filter have in the current result set? ────────
    // Backend now returns disjunctive counts keyed by FILTER KEY (tag-groups pre-intersected), so counts
    // always match what selecting returns. Most filters sort alphabetically (Globo's order); the Size filter
    // uses a logical SHAPE-based order instead — each canonical value's category is inferable from its form
    // (same shapes the backend rule engine produces), so we group by category then sort within, with zero
    // backend/lookup. Sections: Letter → Numeric → Shoe(EU) → Waist → Months → Years → Bangle → Seating →
    // Paper → Other. Within a section: fixed letter order, else the leading number ascending.
    var LETTER_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL', '7XL'];
    function sizeSortKey(v) {
      var U = (v || '').toString().trim().toUpperCase();
      var li = LETTER_ORDER.indexOf(U);
      if (li >= 0) return [0, li];                                              // Letter — fixed size order
      var m;
      if ((m = U.match(/^EU\s*(\d+)$/))) return [2, parseInt(m[1], 10)];        // Shoe (EU)
      if ((m = U.match(/^W-?\s*(\d+)/))) return [3, parseInt(m[1], 10)];        // Waist (first number)
      if (U === 'NEWBORN') return [4, -1];                                       // Months — Newborn first
      if ((m = U.match(/^(\d+).*MONTH/))) return [4, parseInt(m[1], 10)];        // Months (first number)
      if ((m = U.match(/^(\d+).*YEAR/))) return [5, parseInt(m[1], 10)];         // Years (first number)
      if (/^\d+\.\d+$/.test(U)) return [6, parseFloat(U)];                       // Bangle (decimal)
      if ((m = U.match(/^(\d+)\s*SEATER$/))) return [7, parseInt(m[1], 10)];     // Seating
      if ((m = U.match(/^A(\d+)$/))) return [8, parseInt(m[1], 10)];             // Paper/Frame (A0..A5)
      if ((m = U.match(/^(\d+)(?:\s*[-/]\s*\d+)?$/))) return [1, parseInt(m[1], 10)]; // Numeric (bare shoe/waist)
      return [99, 0];                                                            // Other — alpha (tiebreak below)
    }
    function sizeCompare(a, b) {
      var ka = sizeSortKey(a.value), kb = sizeSortKey(b.value);
      if (ka[0] !== kb[0]) return ka[0] - kb[0];
      if (ka[1] !== kb[1]) return ka[1] - kb[1];
      return a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: 'base' });
    }
    function optionsFor(f) {
      var dist = facets[f.key] || {};
      var opts = Object.keys(dist).map(function (v) { return { value: v, count: dist[v] }; })
        .filter(function (o) { return o.count > 0; });
      if (f.key === 'sizes') return opts.sort(sizeCompare);
      return opts.sort(function (a, b) { return a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: 'base' }); });
    }
    // Currency: base prices from the BE are PKR; display in the shopper's active currency like the cards
    // (× Shopify.currency.rate). The slider works in the active currency; filter params convert back to PKR.
    function curRate() { try { return parseFloat((window.Shopify && Shopify.currency && Shopify.currency.rate) || 1) || 1; } catch (e) { return 1; } }
    function curActive() { try { return (window.Shopify && Shopify.currency && Shopify.currency.active) || 'PKR'; } catch (e) { return 'PKR'; } }
    function fmtCur(v) {
      var active = curActive();
      if (active === 'PKR') return 'Rs.' + Math.round(v).toLocaleString('en-US');
      try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: active, maximumFractionDigits: 0 }).format(v); }
      catch (e) { return active + ' ' + Math.round(v).toLocaleString('en-US'); }
    }
    function money(pkr) { return fmtCur(pkr * curRate()); }   // PKR in → active-currency string out

    function isSwatch(f) { return f.facet === 'colors' || f.key === 'colors' || f.key === 'color'; }
    function isSlider(f) { return f.type === 'range'; }
    function selectedVals(k) { return sel[k] || []; }

    // ── Markup (Globo's exact classes/structure) ─────────────────────────────
    function blockOpen(f, extraClass) {
      var id = idsafe(f.key);
      return '<div class="gf-option-block gf-option-' + id + ' ' + extraClass + ' is-collapsed" role="group"' +
               ' data-filter-id="' + id + '" data-filter-key="' + esc(f.key) + '" aria-labelledby="filter-trigger-' + id + '">' +
               '<div class="gf-block-title"><div class="h3" role="button" tabindex="0" aria-expanded="false"' +
                 ' aria-controls="filter-content-' + id + '" id="filter-trigger-' + id + '"><span>' + esc(f.label) + '</span></div>' +
                 '<button type="button" class="tzf-block-clear" data-clearkey="' + esc(f.key) + '">Clear</button></div>' +
               '<div class="gf-block-content" id="filter-content-' + id + '" role="region" aria-labelledby="filter-trigger-' + id + '">';
    }
    function blockClose() { return '</div></div>'; }

    function checkboxOption(k, o) {
      var checked = selectedVals(k).indexOf(o.value) !== -1;
      // Globo shows the tick via a `.checked` class on the <li> (`.checked .gf-Checkbox{background:✓}`).
      return '<li' + (checked ? ' class="checked"' : '') + '><button type="button" aria-label="' + esc(o.value) + '" role="checkbox" aria-checked="' + (checked ? 'true' : 'false') + '"' +
        ' data-fvalue="' + esc(o.value) + '"><span class="gf-Checkbox"></span><span class="gf-label">' + esc(o.value) +
        '</span><span class="gf-count">(' + o.count + ')</span></button></li>';
    }
    function swatchOption(k, o) {
      var checked = selectedVals(k).indexOf(o.value) !== -1;
      var lc = o.value.toLowerCase();
      var rgb = SWATCH[lc];
      // "Multi" (and rainbow/multicolour variants) has no single colour → render a rainbow wheel like Globo.
      var multi = lc.indexOf('multi') !== -1 || lc === 'rainbow' || lc === 'multicolour' || lc === 'multicolor';
      var style = multi
        ? 'background:conic-gradient(#ff0000,#ff9900,#ffee00,#33dd00,#00cccc,#0066ff,#6633ff,#cc00cc,#ff0000)'
        : (rgb ? 'background-color:rgba(' + rgb + ', 1)' : 'background-color:' + esc(o.value)); // fallback: try the name as a CSS colour
      var inner = '<span class="gf-option-one-color" style="' + style + '"></span>';
      return '<li' + (checked ? ' class="checked"' : '') + '><button aria-label="' + esc(o.value) + '" type="button" role="checkbox" aria-checked="' + (checked ? 'true' : 'false') + '"' +
        ' data-fvalue="' + esc(o.value) + '" title="' + esc(o.value) + ' (' + o.count + ')">' + inner + '</button></li>';
    }

    function listBlock(f, options) {
      var id = idsafe(f.key);
      var swatch = isSwatch(f);
      var mods = swatch ? 'gf-option-block-swatch swatch-round none' : 'gf-option-block-checkbox swatch-size-32';
      var searchable = !swatch && options.length > 10;
      var html = blockOpen(f, mods);
      if (searchable) {
        html += '<p id="filter-' + id + '-instructions" class="sr-only">Use this input to search the list of options below.</p>' +
          '<input autocomplete="off" id="filter-' + id + '-search" type="text" aria-label="Search options" placeholder="Search options" value="" class="gf-search">' +
          '<p id="filter-' + id + '-results-count" class="sr-only" aria-live="polite" aria-atomic="true"></p>';
      }
      html += '<div class="gf-scroll' + (swatch ? ' gf-block-no-scroll' : ' gf-block-scroll') + '">' +
        '<ul class="gf-option-box" id="filter-' + id + '-results" aria-labelledby="filter-trigger-' + id + '">';
      var opt = swatch ? swatchOption : checkboxOption;
      for (var i = 0; i < options.length; i++) html += opt(f.key, options[i]);
      html += '</ul></div>' + blockClose();
      return html;
    }

    function sliderBlock(f) {
      var id = idsafe(f.key);
      var rate = curRate();
      var loP = (priceSel && priceSel.min != null) ? priceSel.min : bounds.min;
      var hiP = (priceSel && priceSel.max != null) ? priceSel.max : bounds.max;
      // Inputs + legend display active-currency values; slider works in active currency too.
      // Floor the low bound / ceil the high bound so no product falls outside the range.
      var lo = Math.floor(loP * rate), hi = Math.ceil(hiP * rate);
      return blockOpen(f, 'gf-option-block-slider none') +
        '<div class="gf-scroll"><div class="gf-option-box"><div><div class="gf-range-inputs">' +
          '<label for="min-' + id + '" class="sr-only">' + esc(f.label) + ' Min</label>' +
          '<input class="gf-range-min" id="min-' + id + '" type="text" inputmode="numeric" value="' + lo + '" aria-label="' + esc(f.label) + ' Min">' +
          '<span class="gf-range-split"> - </span>' +
          '<label for="max-' + id + '" class="sr-only">' + esc(f.label) + ' Max</label>' +
          '<input class="gf-range-max" id="max-' + id + '" type="text" inputmode="numeric" value="' + hi + '" aria-label="' + esc(f.label) + ' Max">' +
        '</div><div class="gf-range-slider" id="slider-' + id + '"></div>' +
        '<div class="tzf-range-legend"><span>' + esc(fmtCur(Math.floor(bounds.min * rate))) + '</span><span>' + esc(fmtCur(Math.ceil(bounds.max * rate))) + '</span></div>' +
        '</div></div></div>' + blockClose();
    }

    // ── Render the whole bar ──────────────────────────────────────────────────
    function render() {
      if (!config || !mount) return;
      // Preserve which filter blocks are open + the list scroll position, so a re-render after selecting a
      // value (disjunctive counts change) doesn't collapse the open filter / jump the scroll (reads as "closed").
      var openKeys = [], savedScroll = 0;
      var openBlocks = mount.querySelectorAll('.gf-option-block:not(.is-collapsed)');
      for (var oi = 0; oi < openBlocks.length; oi++) openKeys.push(openBlocks[oi].getAttribute('data-filter-key'));
      var sc = mount.querySelector('.gf-filter-contents'); if (sc) savedScroll = sc.scrollTop;
      // also preserve the chip row's HORIZONTAL scroll, else a re-render (after selecting a value) snaps the bar
      // back to the start and the open chip scrolls off-screen under its still-open dropdown.
      var savedScrollLeft = 0; var siEl = mount.querySelector('.gf-filter-contents-inner'); if (siEl) savedScrollLeft = siEl.scrollLeft;

      // tear down any live sliders before re-render
      Object.keys(sliders).forEach(function (k) { try { sliders[k].destroy(); } catch (e) {} });
      sliders = {};

      var inner = '';
      config.forEach(function (f) {
        if (isSlider(f)) {
          if (bounds.min == null || bounds.max == null || bounds.min >= bounds.max) return; // no usable range → hide
          inner += sliderBlock(f);
        } else {
          var options = optionsFor(f);
          if (!options.length) return;   // no values in this result set → hide the filter
          inner += listBlock(f, options);
        }
      });

      // tzf-inline = single-row scrollable bar (desktop always; mobile only when swimlane, via CSS).
      // tzf-swimlane = admin chose the horizontal chip row on mobile instead of the Filter-button drawer.
      mount.className = 'tzf-bar gf-top_one tzf-inline'
        + (_mobileLayout === 'swimlane' ? ' tzf-swimlane' : '')
        + (mount.classList.contains('tzf-open') ? ' tzf-open' : '');
      // Drawer head/foot render always but show only on mobile (CSS). Chips sit right under the head.
      mount.innerHTML =
        '<div class="tzf-drawer-head"><span>Filter By</span><button type="button" class="tzf-drawer-close" aria-label="Close filter">&times;</button></div>' +
        '<div class="gf-filter-contents gf-grid--1-col"><div class="gf-filter-contents-inner">' + inner + '</div></div>' +
        '<div class="tzf-drawer-foot"><button type="button" class="tzf-drawer-view">View products</button></div>';
      if (typeof _portal !== 'undefined') { _portal.content = _portal.home = _portal.next = _portal.wrap = null; }  // old portaled dropdown was just destroyed

      ensureChrome();
      reflectActive();  // sliders init lazily on first open (see toggleBlock)

      // Restore the previously-open blocks + scroll (so selecting a value doesn't collapse the open filter).
      openKeys.forEach(function (k) {
        var b = mount.querySelector('.gf-option-block[data-filter-key="' + k + '"]');
        if (!b) return;
        b.classList.remove('is-collapsed');
        var h = b.querySelector('.h3'); if (h) h.setAttribute('aria-expanded', 'true');
        // A restored-open slider block has an empty .gf-range-slider (the old one was destroyed) — re-init it,
        // else the price slider vanishes after a re-render while its dropdown is open.
        var sliderEl = b.querySelector('.gf-range-slider');
        if (sliderEl && !sliders[k]) {
          requestAnimationFrame(function () { requestAnimationFrame(function () { if (!sliders[k]) initSlider(k, sliderEl, b); }); });
        }
      });
      var sc2 = mount.querySelector('.gf-filter-contents'); if (sc2 && savedScroll) sc2.scrollTop = savedScroll;
      placeSort();  // re-park the (movable) sort widget — mount.innerHTML above detached it
      setupScroller();  // single-row + ‹ › arrows (desktop always; mobile swimlane)
      var si2 = mount.querySelector('.gf-filter-contents-inner');   // restore horizontal scroll so the open chip
      if (si2 && savedScrollLeft) { si2.scrollLeft = savedScrollLeft; updateArrows(); }   // stays under its dropdown
      pinOpenPanel();   // desktop: re-portal the restored-open dropdown out of the scroll row (no-op on mobile)
    }

    // ── Mobile drawer chrome: a "Filter" trigger + slide-in drawer + a main-screen chips row (desktop hides
    //    trigger/mainchips via CSS; the desktop chips live inside the bar). ──
    var trigger = null, overlay = null, mainChips = null;
    function ensureChrome() {
      if (!trigger && (triggerHost || (mount && mount.parentNode))) {
        trigger = document.createElement('button');
        trigger.type = 'button'; trigger.className = 'tzf-trigger';
        trigger.innerHTML = '<svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M2 4h16v2l-6 6v5l-4-2v-3L2 6V4z"/></svg><span>Filter</span><span class="tzf-trigger-count"></span>';
        trigger.addEventListener('click', openDrawer);
        // With a controls row: prepend so [Filter | Sort]. Otherwise: before the mount (legacy).
        if (triggerHost) triggerHost.insertBefore(trigger, triggerHost.firstChild);
        else mount.parentNode.insertBefore(trigger, mount);
      }
      if (!mainChips && trigger && trigger.parentNode) {
        // Selected-filter chips shown on the MAIN screen (mobile) — same as Globo, so they're visible with
        // the drawer closed. Placed BELOW the controls row (after triggerHost) so it spans full width;
        // legacy path places it right after the trigger. Desktop hides it (chips show in the bar).
        mainChips = document.createElement('div');
        mainChips.className = 'tzf-mainchips';
        mainChips.addEventListener('click', onClick);
        var anchor = triggerHost || trigger;
        if (anchor.parentNode) anchor.parentNode.insertBefore(mainChips, anchor.nextSibling);
      }
      if (!overlay) {
        overlay = document.createElement('div'); overlay.className = 'tzf-overlay';
        overlay.addEventListener('click', closeDrawer);
        document.body.appendChild(overlay);
      }
    }
    // Portal the drawer to <body> while open so position:fixed escapes Dawn's transformed scroll-trigger
    // ancestors (otherwise `fixed` is relative to the transformed container → it renders inline full-width).
    var homeParent = null, homeNext = null;
    function openDrawer() {
      if (!mount) return;
      if (mount.parentNode && mount.parentNode !== document.body) {
        homeParent = mount.parentNode; homeNext = mount.nextSibling;
        document.body.appendChild(mount);
      }
      mount.classList.add('tzf-open');
      if (overlay) overlay.classList.add('tzf-open');
      try { document.documentElement.classList.add('tzf-noscroll'); } catch (e) {}
    }
    function closeDrawer() {
      if (!mount) return;
      mount.classList.remove('tzf-open');
      if (overlay) overlay.classList.remove('tzf-open');
      try { document.documentElement.classList.remove('tzf-noscroll'); } catch (e) {}
      if (homeParent) { try { homeParent.insertBefore(mount, homeNext); } catch (e) {} homeParent = null; homeNext = null; }
    }
    function setTotal(n) {
      if (trigger) { var c = trigger.querySelector('.tzf-trigger-count'); if (c) c.textContent = hasActive() ? '•' : ''; }
      if (mount) { var v = mount.querySelector('.tzf-drawer-view'); if (v) v.textContent = (n != null ? 'View ' + n + ' Products' : 'View products'); }
    }

    // Create one noUiSlider (lazily, when its dropdown first opens so it has a measurable width). The slider
    // works in the ACTIVE currency (PKR bounds × rate); on change we convert back to PKR for the filter.
    function initSlider(key, el, block) {
      if (!window.noUiSlider || bounds.min == null || bounds.max == null || bounds.min >= bounds.max) return;
      var id = idsafe(key);
      var rate = curRate();
      var aMin = Math.floor(bounds.min * rate), aMax = Math.ceil(bounds.max * rate);  // enclose all products
      if (aMin >= aMax) return;
      var lo = (priceSel && priceSel.min != null) ? Math.round(priceSel.min * rate) : aMin;
      var hi = (priceSel && priceSel.max != null) ? Math.round(priceSel.max * rate) : aMax;
      try {
        window.noUiSlider.create(el, { start: [lo, hi], connect: true, step: 1, range: { min: aMin, max: aMax } });
      } catch (e) { return; }
      sliders[key] = el.noUiSlider;
      var minIn = block.querySelector('#min-' + id), maxIn = block.querySelector('#max-' + id);
      el.noUiSlider.on('update', function (vals) {   // active-currency values in the inputs
        if (minIn) minIn.value = Math.round(+vals[0]);
        if (maxIn) maxIn.value = Math.round(+vals[1]);
      });
      // Commit a range given active-currency lo/hi: convert → PKR, set priceSel, commit the filter.
      function commitRange(alo, ahi) {
        var mn = Math.round(alo / rate), mx = Math.round(ahi / rate);
        priceSel = (mn <= bounds.min && mx >= bounds.max) ? null : { min: mn, max: mx };
        commit();
      }
      el.noUiSlider.on('change', function (vals) { commitRange(+vals[0], +vals[1]); });   // drag → commit
      function fromInput() {
        var mn = parseInt(minIn && minIn.value, 10), mx = parseInt(maxIn && maxIn.value, 10);
        if (isNaN(mn)) mn = aMin; if (isNaN(mx)) mx = aMax;
        mn = Math.max(aMin, Math.min(aMax, mn)); mx = Math.max(aMin, Math.min(aMax, mx));  // clamp to bounds
        if (mn > mx) { var t = mn; mn = mx; mx = t; }                                       // keep min ≤ max
        el.noUiSlider.set([mn, mx]);   // moves the bar + syncs both inputs (fires 'update', NOT 'change')
        // Defer the reload one frame: on mobile the 'change' fires as the field blurs, and firing the
        // fetch/re-render mid-blur re-renders the grid while iOS is still zoomed → the grid overflows
        // (horizontal scroll). rAF lets the keyboard drop + zoom reset + viewport settle first.
        if (window.requestAnimationFrame) requestAnimationFrame(function () { commitRange(mn, mx); });
        else commitRange(mn, mx);
      }
      // Enter blurs the field (drops the keyboard + resets iOS zoom) → its 'change' then runs fromInput.
      function onKey(e) { if (e.key === 'Enter' || e.keyCode === 13) { e.preventDefault(); this.blur(); } }
      if (minIn) { minIn.addEventListener('change', fromInput); minIn.addEventListener('keydown', onKey); }
      if (maxIn) { maxIn.addEventListener('change', fromInput); maxIn.addEventListener('keydown', onKey); }
    }

    // Update aria-checked + the block-title selected-count + clear-all visibility, without a rebuild.
    function reflectActive() {
      if (!mount) return;
      config.forEach(function (f) {
        var id = idsafe(f.key);
        var block = mount.querySelector('.gf-option-block[data-filter-id="' + id + '"]');
        if (!block) return;
        var chosen = isSlider(f) ? (priceSel ? 1 : 0) : selectedVals(f.key).length;
        block.classList.toggle('gf-has-selected', chosen > 0);
        // sync checkbox/swatch states — Globo toggles a `.checked` class on the <li>. Scope to data-filter-id
        // (not `block`) so we also reach the option buttons when they're in the portaled desktop dropdown wrapper.
        var btns = mount.querySelectorAll('[data-filter-id="' + id + '"] button[data-fvalue]');
        for (var i = 0; i < btns.length; i++) {
          var on = selectedVals(f.key).indexOf(btns[i].getAttribute('data-fvalue')) !== -1;
          btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
          if (btns[i].parentNode) btns[i].parentNode.classList.toggle('checked', on);
        }
        // a little count next to the label
        var span = block.querySelector('.gf-block-title .h3 > span');
        if (span) span.textContent = chosen > 0 ? f.label + ' (' + chosen + ')' : f.label;
      });
      mount.classList.toggle('tzf-has-active', hasActive());
      renderSelected();
    }

    // ── Selected-item chips (Globo's .globo-selected-items) ───────────────────
    function renderSelected() {
      if (!mount) return;
      var wrap = mount.querySelector('.globo-selected-items-wrapper');
      var items = [];
      config.forEach(function (f) {
        selectedVals(f.key).forEach(function (v) {
          items.push({ key: f.key, label: f.label, value: v });
        });
      });
      if (priceSel) {
        var plo = (priceSel.min != null ? priceSel.min : bounds.min), phi = (priceSel.max != null ? priceSel.max : bounds.max);
        items.push({ key: '__price', label: 'Price', value: money(plo) + ' – ' + money(phi) });
      }

      if (!items.length) {
        if (wrap) wrap.remove();
        if (mainChips) mainChips.innerHTML = '';
        return;
      }
      if (!wrap) {
        wrap = document.createElement('div');
        wrap.className = 'globo-selected-items-wrapper rounded';
        wrap.setAttribute('style', '--text-color:#000000;--icon-color:#959595;--bg-color:#E8E8E8');
        // Placed AFTER the pill list in the DOM → chips render BELOW the filters on desktop (before the
        // results). On mobile the drawer uses flex `order` (CSS) to lift them back to the top of the drawer.
        var contents = mount.querySelector('.gf-filter-contents');
        if (contents) mount.insertBefore(wrap, contents.nextSibling); else mount.appendChild(wrap);
      }
      var clearSvg = '<svg viewBox="0 0 50 50"><path fill="currentColor" d="M25.5 6C15 6 6.5 14.5 6.5 25C6.5 35.5 15 44 25.5 44C36 44 44.5 35.5 44.5 25C44.5 14.5 36 6 25.5 6ZM34.4 31.9L32.5 33.8L25.6 26.9L18.7 33.8L16.8 31.9L23.7 25L16.8 18.1L18.7 16.2L25.6 23.1L32.5 16.2L34.4 18.1L27.4 25L34.4 31.9Z"></path></svg>';
      var lis = items.map(function (it) {
        return '<li class="selected-item gf-option-label"><a tabindex="0" class="refine-by-multi-filter tzf-chip"' +
          ' data-fkey="' + esc(it.key) + '" data-fvalue="' + esc(it.value) + '" aria-label="Remove filter value: ' + esc(it.value) + '">' +
          '<span class="selected-item"><span class="hidden-xs">' + esc(it.label) + '</span><strong><span class="gf-label">' + esc(it.value) + '</span></strong></span>' +
          '<span class="gf-clear">' + clearSvg + '</span></a></li>';
      }).join('');
      var clearAll = '<li class="tzf-clearall-li"><button type="button" class="tzf-clear">Clear all</button></li>';
      var ul = '<ul class="globo-selected-items">' + clearAll + lis + '</ul>';
      wrap.innerHTML = ul;
      // Same chips on the main mobile screen (Globo parity when the drawer is closed).
      if (mainChips) mainChips.innerHTML = '<div class="globo-selected-items-wrapper rounded" style="--text-color:#000000;--icon-color:#959595;--bg-color:#E8E8E8">' + ul + '</div>';
    }

    // ── State changes ─────────────────────────────────────────────────────────
    function toggleValue(key, value) {
      var arr = sel[key] || (sel[key] = []);
      var i = arr.indexOf(value);
      if (i === -1) arr.push(value); else arr.splice(i, 1);
      if (!arr.length) delete sel[key];
      commit();
    }
    // While a mobile bottom sheet is open, DON'T reload per tap — buffer selections (URL + checkbox state
    // still update) and fire one reload when the sheet applies/closes. Desktop/drawer are unchanged (auto-apply).
    var sheetPending = false;
    function commit() { writeUrl(); reflectActive(); if (sheetBlock) sheetPending = true; else onChange(); }
    // Reset every price slider to its FULL range — in the ACTIVE currency (the slider's own units), not PKR.
    function resetSliders() {
      var rate = curRate();
      Object.keys(sliders).forEach(function (k) {
        try { sliders[k].set([Math.floor(bounds.min * rate), Math.ceil(bounds.max * rate)]); } catch (e) {}
      });
    }
    function clear() {
      if (!hasActive()) return;
      sel = {}; priceSel = null;
      resetSliders();
      commit();
    }
    // Per-filter clear (the "Clear" link on a block that has selections).
    function clearFilter(key) {
      if (key === '__price' || key === 'price') { priceSel = null; resetSliders(); }
      else if (sel[key]) delete sel[key];
      else return;
      commit();
    }

    // ── Accordion (open/close a block) — desktop: one open at a time ──────────
    function toggleBlock(block) {
      var wasOpen = !block.classList.contains('is-collapsed');
      // close others (desktop dropdown behaviour)
      var all = mount.querySelectorAll('.gf-option-block');
      for (var i = 0; i < all.length; i++) {
        all[i].classList.add('is-collapsed');
        var t = all[i].querySelector('.h3'); if (t) t.setAttribute('aria-expanded', 'false');
      }
      if (!wasOpen) {
        block.classList.remove('is-collapsed');
        var tr = block.querySelector('.h3'); if (tr) tr.setAttribute('aria-expanded', 'true');
        // Lazily create the noUiSlider AFTER the dropdown has actually laid out — creating it while the
        // container is still display:none gives the pips zero width and they stack vertically. Double-rAF
        // guarantees a reflow first. Only once per slider.
        var key = block.getAttribute('data-filter-key');
        var sliderEl = block.querySelector('.gf-range-slider');
        if (sliderEl && !sliders[key]) {
          requestAnimationFrame(function () { requestAnimationFrame(function () {
            if (!sliders[key]) initSlider(key, sliderEl, block);
          }); });
        }
      }
    }
    function closeAll() {
      if (!mount) return;
      unpinPanel();   // restore any portaled desktop dropdown back into its block
      var all = mount.querySelectorAll('.gf-option-block:not(.is-collapsed)');
      for (var i = 0; i < all.length; i++) {
        all[i].classList.add('is-collapsed');
        var t = all[i].querySelector('.h3'); if (t) t.setAttribute('aria-expanded', 'false');
      }
    }

    // ── Events (delegated on the mount) ───────────────────────────────────────
    function onClick(e) {
      var t = e.target;
      // mobile drawer close / view-products
      if (t.closest && (t.closest('.tzf-drawer-close') || t.closest('.tzf-drawer-view'))) { e.preventDefault(); closeDrawer(); return; }
      // per-filter "Clear" link on a block title
      var bclr = t.closest && t.closest('.tzf-block-clear');
      if (bclr) { e.preventDefault(); e.stopPropagation(); clearFilter(bclr.getAttribute('data-clearkey')); return; }
      // clear all
      if (t.closest && t.closest('.tzf-clear')) { e.preventDefault(); clear(); return; }
      // remove a single selected-item chip (works in the drawer AND the main-screen chips)
      var chip = t.closest && t.closest('.tzf-chip');
      if (chip) {
        e.preventDefault();
        var ck = chip.getAttribute('data-fkey');
        if (ck === '__price') { priceSel = null; resetSliders(); commit(); }
        else toggleValue(ck, chip.getAttribute('data-fvalue'));
        return;
      }
      // option toggle
      var optBtn = t.closest && t.closest('button[data-fvalue]');
      if (optBtn && mount.contains(optBtn)) {
        e.preventDefault();
        var block = optBtn.closest('.gf-option-block');
        toggleValue(block.getAttribute('data-filter-key'), optBtn.getAttribute('data-fvalue'));
        return;
      }
      // accordion trigger
      var trig = t.closest && t.closest('.gf-block-title');
      if (trig && mount.contains(trig)) {
        e.preventDefault();
        var blk = trig.closest('.gf-option-block');
        var willOpen = blk.classList.contains('is-collapsed');
        toggleBlock(blk);
        if (isMobileSheet()) {
          // mobile swimlane → open/close the bottom sheet (toggleBlock set the collapsed state)
          if (willOpen) openSheet(blk); else closeSheet();
        } else {
          pinOpenPanel();   // desktop → pinned dropdown
        }
        return;
      }
    }
    function onInput(e) {
      var s = e.target;
      if (!s.classList || !s.classList.contains('gf-search')) return;
      var q = s.value.trim().toLowerCase();
      var ul = s.parentNode.querySelector('.gf-option-box') || (s.closest('.gf-block-content') && s.closest('.gf-block-content').querySelector('.gf-option-box'));
      if (!ul) return;
      var lis = ul.querySelectorAll('li');
      for (var i = 0; i < lis.length; i++) {
        var lab = (lis[i].querySelector('.gf-label') || {}).textContent || lis[i].textContent;
        lis[i].style.display = (!q || lab.toLowerCase().indexOf(q) !== -1) ? '' : 'none';
      }
    }
    function onDocClick(e) {
      if (mount && !mount.contains(e.target)) closeAll();
      if (sortEl && sortEl.classList.contains('active') && !sortEl.contains(e.target)) {
        sortEl.classList.remove('active');
        var tg = sortEl.querySelector('.sort-by-toggle'); if (tg) tg.setAttribute('aria-expanded', 'false');
      }
    }
    function onKey(e) { if (e.key === 'Escape') { closeAll(); if (sortEl) sortEl.classList.remove('active'); } }

    // ── Sort widget (Globo's .sort-by dropdown; styled by the copied globo CSS) ──
    var sortEl = null, sortHome = null;
    // renderSort builds a MOVABLE sort widget. On desktop it's parked at the right of the filter pill
    // rows (inside .gf-filter-contents, Globo-style); on mobile it lives in the controls row (`container`,
    // beside the Filter trigger). placeSort() decides which, and re-runs after every render() + on resize.
    function renderSort(container) {
      if (!container) return;
      sortHome = container;
      if (!sortEl) {
        sortEl = document.createElement('div');
        sortEl.className = 'sort-by tzf-sort';
        sortEl.addEventListener('click', function (e) {
          var opt = e.target.closest && e.target.closest('li[data-sort]');
          if (opt) { e.preventDefault(); setSort(opt.getAttribute('data-sort')); sortEl.classList.remove('active'); return; }
          var tog = e.target.closest && e.target.closest('.sort-by-toggle');
          if (tog) { e.preventDefault(); sortEl.classList.toggle('active'); tog.setAttribute('aria-expanded', sortEl.classList.contains('active') ? 'true' : 'false'); }
        });
        window.addEventListener('resize', placeSort, { passive: true });
      }
      var lis = SORT_OPTIONS.map(function (o) {
        return '<li role="option" tabindex="-1" data-sort="' + esc(o.v) + '"' + (o.v === sortValue ? ' aria-selected="true"' : '') + '>' + esc(o.label) + '</li>';
      }).join('');
      sortEl.innerHTML =
        '<button type="button" class="sort-by-toggle" aria-expanded="false" aria-haspopup="listbox">Sort By: <span class="tzf-sort-current">' + esc(sortLabel(sortValue)) + '</span></button>' +
        '<ul class="globo-dropdown-custom__options" role="listbox">' + lis + '</ul>';
      placeSort();
    }
    // Desktop → into .gf-filter-contents (position:relative; sort is absolutely parked bottom-right via CSS).
    // Mobile → back to the controls row. Idempotent: only moves when the parent differs.
    function placeSort() {
      // Sort always lives in its host slot (the count row) — desktop no longer parks it inside the filter
      // bar (that overlapped the now-full-width single-row filters).
      if (!sortEl || !sortHome) return;
      if (sortEl.parentNode !== sortHome) sortHome.appendChild(sortEl);
    }
    function setSort(v) {
      v = v || '';
      if (v === sortValue) { return; }
      sortValue = v;
      if (sortEl) {
        var cur = sortEl.querySelector('.tzf-sort-current'); if (cur) cur.textContent = sortLabel(sortValue);
        var lis = sortEl.querySelectorAll('li[data-sort]');
        for (var i = 0; i < lis.length; i++) lis[i].setAttribute('aria-selected', lis[i].getAttribute('data-sort') === sortValue ? 'true' : 'false');
      }
      writeUrl();
      onChange();
    }

    // ── Facets in (from the host after each page-1 response) ──────────────────
    function setFacets(f, pmin, pmax) {
      facets = f || {};
      bounds = { min: pmin != null ? Math.floor(pmin) : null, max: pmax != null ? Math.ceil(pmax) : null };
      var sig = JSON.stringify(Object.keys(facets).sort().map(function (k) {
        return [k, Object.keys(facets[k] || {}).sort()];
      })) + '|' + bounds.min + '|' + bounds.max;
      if (sig === facetSig) { reflectActive(); return; } // same query → options unchanged, just refresh state
      facetSig = sig;
      render();
    }

    // ── Single-row bar + ‹ › arrows (desktop always; mobile only when swimlane) ───────────────
    // Filter blocks are absolute dropdowns, so a plain overflow-x row would clip an OPEN panel. We
    // horizontal-scroll the row AND pin the open panel to `fixed` under its trigger to escape the clip.
    function ensureLayoutCss() {
      if (document.getElementById('tzf-layout-css')) return;
      var css =
        // desktop: force ONE scrollable row
        '@media (min-width:769px){' +
          '.tzf-bar.gf-top_one.tzf-inline .gf-filter-contents{display:block!important;position:relative!important;overflow:visible!important;padding:0!important;margin:0!important}' +
          // Full reset of Globo\'s absolute-dropdown styling on the inner so it becomes a plain static flex row
          // flush-left (was position:absolute;left/right/top;border;padding — the real cause of the offset).
          '.tzf-bar.gf-top_one.tzf-inline .gf-filter-contents .gf-filter-contents-inner{position:static!important;left:auto!important;right:auto!important;top:auto!important;bottom:auto!important;transform:none!important;display:flex!important;flex-wrap:nowrap!important;gap:8px;overflow-x:auto!important;overflow-y:visible!important;scroll-behavior:smooth;scrollbar-width:none;-ms-overflow-style:none;padding:2px 0!important;margin:0!important;width:auto!important;max-width:none!important;background:transparent!important;border:0!important;box-shadow:none!important;border-radius:0!important;opacity:1!important;visibility:visible!important;z-index:auto!important}' +
          '.tzf-bar.gf-top_one.tzf-inline .gf-filter-contents-inner::-webkit-scrollbar{display:none}' +
          '.tzf-bar.gf-top_one.tzf-inline .gf-filter-contents .gf-filter-contents-inner>.gf-option-block{flex:0 0 auto!important;margin:0!important}' +
        '}' +
        // mobile swimlane: force the (normally drawer-hidden) bar to show inline as a horizontal chip row,
        // and hide the Filter trigger + drawer chrome. Aggressive !important resets override the drawer CSS.
        '@media (max-width:768px){' +
          '.tzf-bar.tzf-swimlane{position:static!important;transform:none!important;display:block!important;width:auto!important;max-width:none!important;height:auto!important;max-height:none!important;inset:auto!important;background:transparent!important;box-shadow:none!important;visibility:visible!important;opacity:1!important;padding:0!important;margin:0 0 8px!important;border:0!important;z-index:auto!important}' +
          '.tzf-swimlane .tzf-drawer-head,.tzf-swimlane .tzf-drawer-foot{display:none!important}' +
          // position:relative confines the scroll-fade ::before/::after (top:0;bottom:0;width:52px) to THIS bar.
          // Without it the element is static, so those fade strips anchor to a tall page ancestor and paint a
          // white haze down the whole left/right edge of the product grid (tracked the scroll-overflow side).
          '.tzf-swimlane .gf-filter-contents{position:relative!important;display:block!important;overflow:visible!important;height:auto!important;max-height:none!important;padding:0!important}' +
          '.tzf-swimlane .gf-filter-contents-inner{display:flex!important;flex-wrap:nowrap!important;gap:8px;overflow-x:auto;overflow-y:visible;scrollbar-width:none;padding:2px 8px;margin:0!important}' +
          '.tzf-swimlane .gf-filter-contents-inner::-webkit-scrollbar{display:none}' +
          '.tzf-swimlane .gf-filter-contents-inner>.gf-option-block{flex:0 0 auto;width:auto!important;min-width:0!important;margin:0!important}' +
          'html.tzf-swimlane-on .tzf-trigger,html.tzf-swimlane-on .tzf-mainchips{display:none!important}' +
          // Hide the DRAWER design\'s per-filter red "Clear" link (taminzo-search-v2.css :285, shown on a
          // selected block) in swim-lane — clearing is done via the bottom sheet Reset + the chip\'s ×.
          'html.tzf-swimlane-on .tzf-block-clear{display:none!important}' +
        '}' +
        // arrows + pinned open panel
        '.tzf-scroll-wrap .tzf-arrow{position:absolute;top:50%;transform:translateY(-50%);width:34px;height:34px;z-index:20;display:none;align-items:center;justify-content:center;background:#fff;border:1px solid #e4e4e4;border-radius:50%;cursor:pointer;color:#1a1a1a;padding:0;box-shadow:0 2px 8px rgba(0,0,0,.14);transition:background .15s ease,box-shadow .15s ease,border-color .15s ease}' +
        '.tzf-scroll-wrap .tzf-arrow:hover{background:#111;border-color:#111;color:#fff;box-shadow:0 3px 12px rgba(0,0,0,.22)}' +
        '.tzf-scroll-wrap .tzf-arrow svg{width:15px;height:15px;display:block;pointer-events:none}' +
        '.tzf-scroll-wrap.tzf-ov-left .tzf-arrow-left{display:flex}' +
        '.tzf-scroll-wrap.tzf-ov-right .tzf-arrow-right{display:flex}' +
        '.tzf-arrow-left{left:2px}' +
        '.tzf-arrow-right{right:2px}' +
        // white fade so the edge filter dissolves under the arrow instead of being hard-cut
        '.tzf-scroll-wrap.tzf-ov-left::before{content:"";position:absolute;top:0;bottom:0;left:0;width:52px;pointer-events:none;z-index:15;background:linear-gradient(to left,transparent,#fff 72%)}' +
        '.tzf-scroll-wrap.tzf-ov-right::after{content:"";position:absolute;top:0;bottom:0;right:0;width:52px;pointer-events:none;z-index:15;background:linear-gradient(to right,transparent,#fff 72%)}' +
        // mobile = touch: no arrow buttons (users swipe the chip row); keep the edge fades as the scroll hint.
        '@media (max-width:768px){.tzf-scroll-wrap .tzf-arrow{display:none!important}}' +
        // mobile: slimmer, less-washed edge fade so the NEXT chip clearly peeks (a cut-off chip is the clearest
        // "swipe for more" cue) instead of a wide blur over the whole last chip.
        '@media (max-width:768px){.tzf-scroll-wrap.tzf-ov-left::before{width:20px;background:linear-gradient(to left,transparent,#fff 90%)}.tzf-scroll-wrap.tzf-ov-right::after{width:20px;background:linear-gradient(to right,transparent,#fff 90%)}}' +
        // iOS Safari only SYNTHESIZES a click (for our delegated handlers) on a tap when the tapped element is
        // "clickable" — cursor:pointer makes plain divs qualify. Without this, tapping a chip/option on a REAL
        // iPhone never fires click, so the bottom sheet / selection does nothing (works in the desktop emulator
        // because that uses real mouse events).
        '.tzf-bar .gf-block-title,.tzf-bar .gf-block-title .h3,.tzf-bar .gf-option-box li,.tzf-bar .gf-option-value,.tzf-bar button[data-fvalue],.tzf-bar .tzf-chip,.tzf-bar [role="button"]{cursor:pointer}' +
        // iOS tints the option <a> links its default blue; Chrome/Android inherit dark. Force dark via
        // -webkit-text-fill-color (what iOS actually honors for links) so the sheet/dropdown text matches.
        '.tzf-bar .gf-option-box a,.tzf-bar .gf-option-box li,.tzf-bar .gf-option-value,.tzf-bar .gf-label{color:#222!important;-webkit-text-fill-color:#222!important}' +
        // The open dropdown is portaled to the bar root inside .tzf-portal-wrap (which mirrors the block classes
        // so Globo option CSS still applies). The WRAPPER is the fixed panel; its .gf-block-content/.gf-scroll
        // are reset to plain flow so only the wrapper draws the panel chrome and .gf-scroll scrolls inside it.
        '.tzf-bar .tzf-portal-wrap{position:fixed!important;z-index:70;background:#fff;border:1px solid #ececec;border-radius:5px;box-shadow:0 8px 24px rgba(0,0,0,.16);width:max-content!important;min-width:200px!important;max-width:min(300px,92vw)!important;max-height:60vh;display:flex!important;flex-direction:column!important;padding:8px 12px!important;float:none!important;margin:0!important}' +
        '.tzf-bar .tzf-portal-wrap .gf-block-content{position:static!important;display:flex!important;flex-direction:column!important;flex:1 1 auto;min-height:0;max-height:none!important;overflow:visible!important;width:auto!important;padding:0!important;margin:0!important;border:0!important;box-shadow:none!important;background:none!important}' +
        '.tzf-bar .tzf-portal-wrap .gf-scroll{position:static!important;flex:1 1 auto;min-height:0;max-height:none!important;overflow-y:auto!important;overflow-x:hidden!important;width:auto!important;padding:0!important;margin:0!important}' +
        // modest breathing room inside the option list (was flush to the panel edge)
        '.tzf-bar .tzf-portal-wrap .gf-option-box{padding:0!important;margin:0!important}' +
        '.tzf-bar .tzf-portal-wrap .gf-option-box>li{padding:2px 0!important;margin:0!important}' +
        '.tzf-bar .tzf-portal-wrap .gf-search{margin-bottom:6px}' +
        // slimmer filter pills (Globo default 5px 20px felt too big)
        '@media (min-width:769px){.tzf-bar.gf-top_one.tzf-inline .gf-option-block .gf-block-title .h3{padding:5px 14px!important}}' +
        // ── Mobile bottom sheet (swimlane): tapping a chip opens the options as a slide-up sheet ──
        // Sheet + backdrop sit ABOVE the bottom nav bar (--tzf-navh, measured in JS incl. iOS safe-area), and
        // while a sheet is open we un-trap .tz-results-wrap (z-index:0) so the fixed sheet can paint over the nav.
        '.tzf-sheet-backdrop{position:fixed;top:0;left:0;right:0;bottom:var(--tzf-navh,0px);background:rgba(0,0,0,.45);z-index:9998;opacity:0;transition:opacity .25s ease;pointer-events:none}' +
        '.tzf-sheet-backdrop.tzf-open{opacity:1;pointer-events:auto}' +
        'html.tzf-sheet-active .tz-results-wrap{z-index:auto!important}' +
        // WebKit (real iOS Safari) CLIPS the fixed bottom-sheet to the chip row\'s overflow-x:auto box (a thin
        // strip at the top) → the sheet renders off in that strip and looks like it never opened. While a sheet
        // is open the page is already locked (tzf-noscroll + backdrop), so drop that overflow clip → sheet paints.
        'html.tzf-sheet-active .tzf-swimlane .gf-filter-contents-inner{overflow:visible!important}' +
        '@media (max-width:768px){' +
          // the SHEET is .gf-block-content (holds BOTH the search box and the options) — not just .gf-scroll,
          // else the search input leaks out into the chip and widens it.
          // Flex column: header + search stay at top, options scroll in the MIDDLE, footer is a solid bar at
          // the bottom (own flex row, never scrolls) — so nothing shows through behind the buttons.
          '.tzf-swimlane .gf-option-block.tzf-sheet .gf-block-content{position:fixed!important;left:0!important;right:0!important;bottom:var(--tzf-navh,0px)!important;top:auto!important;width:100%!important;max-width:none!important;min-width:0!important;max-height:75vh!important;border-radius:16px 16px 0 0!important;background:#fff!important;box-shadow:0 -6px 28px rgba(0,0,0,.2)!important;z-index:9999!important;padding:0 16px!important;overflow:hidden!important;display:flex!important;flex-direction:column!important;transform:translateY(120%);transition:transform .3s cubic-bezier(.4,0,.2,1)!important}' +
          '.tzf-swimlane .gf-option-block.tzf-sheet.tzf-sheet-up .gf-block-content{transform:translateY(0)!important}' +
          '.tzf-swimlane .gf-option-block.tzf-sheet .gf-block-content>*{flex:0 0 auto}' +
          // max-height:none removes Globo\'s cap on the options list so a long list fills the sheet up to its
          // 75vh max (was leaving the sheet ~50-55vh because the capped list couldn\'t expand it).
          '.tzf-swimlane .gf-option-block.tzf-sheet .gf-block-content .gf-scroll{flex:1 1 auto!important;min-height:0!important;max-height:none!important;overflow-y:auto!important;overflow-x:hidden!important;position:static!important;padding:0!important;margin:0!important}' +
          // search box: extra LEFT padding so the text clears the magnifier icon Globo draws at the left
          '.tzf-swimlane .gf-option-block.tzf-sheet .gf-block-content .gf-search{display:block!important;width:100%!important;box-sizing:border-box!important;margin:10px 0!important;padding:11px 14px 11px 40px!important;border:1px solid #e0e0e0!important;border-radius:8px!important;font-size:15px!important;background-position:14px center!important}' +
          '.tzf-swimlane .gf-option-block.tzf-sheet .gf-block-content .gf-option-box>li{padding:6px 0!important}' +
          // footer: fully OPAQUE + full-bleed white so scrolling options never show through the buttons
          '.tzf-swimlane .gf-option-block.tzf-sheet .tzf-sheet-foot{position:static!important;background:#fff!important;margin:0 -16px!important;padding:14px 16px calc(10px + var(--tzf-safe, env(safe-area-inset-bottom,0px)))!important;box-shadow:0 -3px 10px rgba(0,0,0,.08)}' +
          '.tzf-swimlane .gf-option-block.tzf-sheet .tzf-sheet-head{position:static!important;margin:0 -16px 6px!important;padding:16px 16px 12px!important}' +
        '}' +
        // trim the large empty gap between the "Search results" title and the filter bar on mobile:
        // the culprit is the REDUNDANT second search form (.template-search__search, below the h1 — there is
        // already a search box in the site header). Hide it on the results view; also tighten header spacing.
        '@media (max-width:768px){' +
          // THE gap: .tz-controls is the container for the "Filter" drawer button — empty (button hidden) in
          // swim-lane mode but still display:flex + min-height:3.8rem + margin. Hide the whole box in swim-lane.
          'html.tzf-swimlane-on .tz-controls{display:none!important}' +
          '.tz-results-active .template-search__search{display:none!important}' +
          '.tz-results-active .template-search__header{margin-bottom:0.6rem!important}' +
          '.tz-results-active .template-search--empty{padding-bottom:0!important}' +
          '.tz-results-active #ProductGridContainer,.tz-results-active #product-grid{margin-top:0!important;padding-top:0!important;min-height:0!important}' +
          // swim-lane chips: restore the bordered "pill" look (mobile drawer CSS had stripped the border)
          '.tzf-bar.tzf-swimlane .gf-option-block .gf-block-title .h3{border:1px solid #e0e0e0!important;border-radius:5px!important;padding:8px 14px!important;background:#fff!important;color:#222!important;font-weight:400!important}' +
          // kill the DRAWER-only divider lines that bleed into the horizontal chip row (short line under each
          // chip, full-width line under the selected-chips row) — they belong to the vertical drawer list only.
          '.tzf-bar.tzf-swimlane .gf-option-block{border-bottom:0!important}' +
          '.tzf-bar.tzf-swimlane .globo-selected-items-wrapper{border-bottom:0!important}' +
        '}' +
        '.tzf-sheet-head{position:sticky;top:0;background:#fff;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:16px 0 12px;margin:0 0 4px;border-bottom:1px solid #eee;font-weight:600;font-size:16px;z-index:2}' +
        '.tzf-sheet-close{background:none;border:0;font-size:26px;line-height:1;cursor:pointer;padding:0 6px;color:#333}' +
        '.tzf-sheet-foot{position:sticky;bottom:0;background:#fff;display:flex;gap:12px;padding:14px 0 6px;margin-top:10px;border-top:1px solid #eee;z-index:2}' +
        '.tzf-sheet-foot button{padding:14px;border-radius:10px;font-size:15px;font-weight:700;cursor:pointer;line-height:1}' +
        '.tzf-sheet-reset{flex:0 0 34%;background:#fff;border:1px solid #d0d0d0;color:#222}' +
        '.tzf-sheet-apply{flex:1 1 auto;background:#1a1a1a;border:1px solid #1a1a1a;color:#fff}';
      var s = document.createElement('style'); s.id = 'tzf-layout-css'; s.textContent = css;
      document.head.appendChild(s);
    }

    function isInlineActive() {
      // inline row is showing when: NOT the mobile drawer (tzf-open), and (desktop OR swimlane).
      if (mount.classList.contains('tzf-open')) return false;
      var mobile = window.matchMedia && window.matchMedia('(max-width:768px)').matches;
      return mobile ? _mobileLayout === 'swimlane' : true;
    }
    function isMobile() { return window.matchMedia && window.matchMedia('(max-width:768px)').matches; }
    function isMobileSheet() { return isMobile() && _mobileLayout === 'swimlane'; }

    // ── Mobile bottom sheet: on swimlane, a tapped filter opens its options as a slide-up sheet ──
    var sheetBackdrop = null, sheetBlock = null, sheetSnapshot = null;
    function ensureSheetBackdrop() {
      if (sheetBackdrop) return sheetBackdrop;
      sheetBackdrop = document.createElement('div');
      sheetBackdrop.className = 'tzf-sheet-backdrop';
      sheetBackdrop.addEventListener('click', function () { closeSheet(); });
      document.body.appendChild(sheetBackdrop);
      return sheetBackdrop;
    }
    function openSheet(block) {
      if (!block) return;
      if (sheetBlock && sheetBlock !== block) closeSheet(true);
      var panel = block.querySelector('.gf-block-content');   // the whole panel (search + options) = the sheet
      if (!panel) return;
      sheetBlock = block;
      sheetSnapshot = JSON.parse(JSON.stringify(sel));   // pre-sheet selection — restored on Cancel (discard)
      // header (filter name + ✕) injected once per open
      if (!panel.querySelector('.tzf-sheet-head')) {
        var raw = (block.querySelector('.gf-block-title .h3 > span') || {}).textContent || 'Filter';
        var label = raw.replace(/\s*\(\d+\)\s*$/, '');
        var head = document.createElement('div');
        head.className = 'tzf-sheet-head';
        head.innerHTML = '<span>' + esc(label) + '</span><button type="button" class="tzf-sheet-close" aria-label="Close">&times;</button>';
        head.querySelector('.tzf-sheet-close').addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); closeSheet(); });
        panel.insertBefore(head, panel.firstChild);
      }
      // footer (Reset + Apply) injected once per open
      if (!panel.querySelector('.tzf-sheet-foot')) {
        var foot = document.createElement('div');
        foot.className = 'tzf-sheet-foot';
        foot.innerHTML = '<button type="button" class="tzf-sheet-reset">Reset</button><button type="button" class="tzf-sheet-apply">Apply</button>';
        foot.querySelector('.tzf-sheet-reset').addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); resetSheetFilter(); });
        foot.querySelector('.tzf-sheet-apply').addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); closeSheet(false, true); });
        panel.appendChild(foot);
      }
      // Position the sheet above the bottom nav. In the APP there is no web sticky nav (the app uses a NATIVE
      // nav and the WebView already sits above it), so open flush from bottom:0 — reserving the web nav's height
      // there just left an empty gap. In the BROWSER, sit above the web sticky nav (measured live; fall back to
      // the iOS safe-area inset). The foot keeps its env(safe-area-inset-bottom) padding either way.
      var navH, inApp = window.isTaminzoApp || window.isAppUser;
      if (inApp) {   // in-app (iOS or Android): open flush from the bottom
        navH = '0px';
      } else {
        var nav = document.getElementById('sticky-bottom-nav-mobile');
        navH = (nav && nav.offsetParent !== null && nav.offsetHeight > 0) ? nav.offsetHeight + 'px' : 'env(safe-area-inset-bottom, 0px)';
      }
      try {
        document.documentElement.style.setProperty('--tzf-navh', navH);
        // Foot safe-area inset: the APP WebView handles system bars (and Android over-reports
        // env(safe-area-inset-bottom) → a big white gap under the buttons), so zero it in-app; browser keeps it.
        if (inApp) document.documentElement.style.setProperty('--tzf-safe', '0px');
        else document.documentElement.style.removeProperty('--tzf-safe');
      } catch (e) {}
      block.classList.add('tzf-sheet');
      try { document.documentElement.classList.add('tzf-sheet-active'); } catch (e) {}
      ensureSheetBackdrop().classList.add('tzf-open');
      try { document.documentElement.classList.add('tzf-noscroll'); } catch (e) {}
      requestAnimationFrame(function () { block.classList.add('tzf-sheet-up'); });   // slide up
    }
    function resetSheetFilter() {
      if (!sheetBlock) return;
      var key = sheetBlock.getAttribute('data-filter-key');
      if (key && sel[key]) { delete sel[key]; commit(); }   // commit defers (sheet open); checkboxes clear via reflectActive
    }
    // doApply=true (Apply button) → keep the buffered selection and reload once. Otherwise (✕, tap-outside,
    // re-tap, switching) → CANCEL: revert the selection to the pre-sheet snapshot, no reload.
    function closeSheet(immediate, doApply) {
      var block = sheetBlock; sheetBlock = null;
      if (sheetBackdrop) sheetBackdrop.classList.remove('tzf-open');
      try { document.documentElement.classList.remove('tzf-noscroll'); document.documentElement.classList.remove('tzf-sheet-active'); } catch (e) {}
      var hadPending = sheetPending; sheetPending = false;
      var snap = sheetSnapshot; sheetSnapshot = null;
      var reload = false;
      if (doApply) {
        reload = hadPending;                                   // commit the buffered changes (reload after anim)
      } else if (hadPending && snap) {
        sel = snap; writeUrl(); reflectActive();               // CANCEL: discard buffered changes now, no reload
      }
      var afterAnim = function () { if (reload) onChange(); };
      if (!block) { afterAnim(); return; }
      block.classList.remove('tzf-sheet-up');   // slide down
      var finish = function () {
        block.classList.remove('tzf-sheet');
        block.classList.add('is-collapsed');
        var h = block.querySelector('.h3'); if (h) h.setAttribute('aria-expanded', 'false');
        var panel = block.querySelector('.gf-block-content');
        if (panel) { var head = panel.querySelector('.tzf-sheet-head'); if (head) head.remove(); var ft = panel.querySelector('.tzf-sheet-foot'); if (ft) ft.remove(); }
        afterAnim();
      };
      if (immediate) finish(); else setTimeout(finish, 300);
    }

    function setupScroller() {
      ensureLayoutCss();
      try { document.documentElement.classList.toggle('tzf-swimlane-on', _mobileLayout === 'swimlane'); } catch (e) {}
      var contents = mount.querySelector('.gf-filter-contents');
      var inner = mount.querySelector('.gf-filter-contents-inner');
      if (!contents || !inner) return;
      contents.classList.add('tzf-scroll-wrap');
      if (!contents.querySelector('.tzf-arrow-left')) {
        var chev = function (d) { return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="' + d + '"/></svg>'; };
        var mk = function (cls, dir) {
          var b = document.createElement('button');
          b.type = 'button'; b.className = 'tzf-arrow ' + cls;
          b.innerHTML = chev(dir < 0 ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6');
          b.setAttribute('aria-label', dir < 0 ? 'Scroll filters left' : 'Scroll filters right');
          b.addEventListener('click', function (e) {
            e.preventDefault(); e.stopPropagation();
            inner.scrollBy({ left: dir * Math.round(inner.clientWidth * 0.7), behavior: 'smooth' });
          });
          return b;
        };
        contents.appendChild(mk('tzf-arrow-left', -1));
        contents.appendChild(mk('tzf-arrow-right', 1));
        inner.addEventListener('scroll', function () { updateArrows(); pinOpenPanel(); });
      }
      updateArrows();
    }
    function updateArrows() {
      var contents = mount.querySelector('.gf-filter-contents'), inner = mount.querySelector('.gf-filter-contents-inner');
      if (!contents || !inner) return;
      var ov = inner.scrollWidth - inner.clientWidth > 2;
      contents.classList.toggle('tzf-ov-left', ov && inner.scrollLeft > 2);
      contents.classList.toggle('tzf-ov-right', ov && inner.scrollLeft < (inner.scrollWidth - inner.clientWidth - 2));
    }
    // The open dropdown's .gf-block-content, portaled out of the scroll row to the bar root (see pinOpenPanel).
    var _portal = { content: null, home: null, next: null, wrap: null };
    function unpinPanel() {
      var pinned = mount.querySelectorAll('.gf-option-block.tzf-pinned');
      for (var i = 0; i < pinned.length; i++) pinned[i].classList.remove('tzf-pinned');
      if (_portal.content) {
        try { _portal.home.insertBefore(_portal.content, _portal.next); } catch (e) {}   // move content back into its block
        if (_portal.wrap && _portal.wrap.parentNode) _portal.wrap.parentNode.removeChild(_portal.wrap);
        _portal.content = _portal.home = _portal.next = _portal.wrap = null;
      }
    }
    // Pin the single open dropdown to fixed under its trigger. The chip row (.gf-filter-contents-inner) has
    // overflow-x:auto, and Safari CLIPS a position:fixed descendant to an overflow ancestor (Chrome doesn't) —
    // so we PORTAL the dropdown up to the bar root (`mount`), out of the scroll row, where nothing clips it. We
    // move the whole .gf-block-content into a wrapper that CARRIES THE BLOCK'S CLASSES (gf-option-block-checkbox,
    // swatch-*, …) so Globo's block-scoped option CSS still applies. It stays inside `mount`, so the delegated
    // onClick/onInput handlers keep working for its options + search.
    function pinOpenPanel() {
      unpinPanel();
      if (isMobile()) return;   // mobile uses the bottom sheet (swimlane) or the drawer, never the desktop pin
      if (!isInlineActive()) return;
      var block = mount.querySelector('.gf-option-block:not(.is-collapsed)');
      if (!block) return;
      var title = block.querySelector('.gf-block-title'), content = block.querySelector('.gf-block-content');
      if (!title || !content) return;
      var cls = block.className;   // capture BEFORE adding tzf-pinned so the wrapper mirrors Globo's block classes
      block.classList.add('tzf-pinned');
      var wrap = document.createElement('div');
      wrap.className = cls + ' tzf-portal-wrap';
      // The wrapper carries .gf-option-block, so optBtn.closest('.gf-option-block') now resolves to it — it must
      // also carry the block's data-* (esp. data-filter-key) or the option-toggle reads a null key = un-clickable.
      var dk = block.getAttribute('data-filter-key'); if (dk) wrap.setAttribute('data-filter-key', dk);
      var di = block.getAttribute('data-filter-id'); if (di) wrap.setAttribute('data-filter-id', di);
      _portal.content = content; _portal.home = content.parentNode; _portal.next = content.nextSibling; _portal.wrap = wrap;
      wrap.appendChild(content);
      mount.appendChild(wrap);   // escape the overflow-x:auto scroll row (Safari fixed-clip fix)
      // Anchor the wrapper to the button. If a full-width (300px) panel fits to the RIGHT of the button, left-align
      // (CSS `left`). Otherwise anchor its RIGHT edge to the button's right edge (CSS `right`) — using `right`
      // means NO panel-width measurement, so it's correct on the FIRST open too (no layout-timing race).
      var r = title.getBoundingClientRect();
      var vw = window.innerWidth, gap = 8, PANEL_MAX = 300;
      wrap.style.top = Math.round(r.bottom + 2) + 'px';
      if (r.left + PANEL_MAX <= vw - gap) {
        wrap.style.right = 'auto';
        wrap.style.left = Math.round(Math.max(gap, r.left)) + 'px';
      } else {
        wrap.style.left = 'auto';
        wrap.style.right = Math.round(Math.max(gap, vw - r.right)) + 'px';
      }
    }

    // ── Boot ──────────────────────────────────────────────────────────────────
    if (mount) {
      mount.className = 'tzf-bar gf-top_one';
      mount.addEventListener('click', onClick);
      mount.addEventListener('input', onInput);
      // Use mousedown, not click: Safari doesn't fire click for taps on non-interactive/empty areas (product
      // grid, blank space), so a click-based outside-close never runs there; mousedown fires reliably on all.
      document.addEventListener('mousedown', onDocClick, true);
      document.addEventListener('keydown', onKey);
      window.addEventListener('resize', function () { updateArrows(); pinOpenPanel(); });
      // Close the (position:fixed) desktop dropdown when the PAGE scrolls — otherwise it stays pinned to the
      // viewport while the bar scrolls away, floating detached. Only fires on document scroll, not when scrolling
      // inside the dropdown's own list (element scroll doesn't bubble to window); no-op on mobile (sheet path).
      window.addEventListener('scroll', function () { if (_portal.content) closeAll(); }, { passive: true });
    }
    readUrl();  // SYNCHRONOUS — so getParams()/resultsPath() carry the URL filters on the host's first fetch
    loadConfig(api).then(function (cfg) {
      config = cfg || [];
      configKeys = config.map(function (f) { return f.key; });
      if (facetSig) render();
    });

    return {
      getParams: getParams, resultsPath: resultsPath, setBase: setBase,
      setFacets: setFacets, hasActive: hasActive, clear: clear, close: closeAll,
      openDrawer: openDrawer, closeDrawer: closeDrawer, setTotal: setTotal,
      renderSort: renderSort, setSort: setSort, el: mount
    };
  }

  window.TaminzoFilters = { create: create };
})();
